import CodeMirror from '@uiw/react-codemirror';
import { json } from '@codemirror/lang-json';
import { EditorView } from '@codemirror/view';
import { Prec } from '@codemirror/state';
import { HighlightStyle, syntaxHighlighting } from '@codemirror/language';
import { tags as t } from '@lezer/highlight';
import { useTheme } from 'components/core/theme-provider';
import { cn } from 'lib/utils';

type JsonCodeEditorProps = {
  id: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  minHeight?: string;
  hasError?: boolean;
};

const monoFont =
  "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace";

/** Light theme — horizontal padding on .cm-line only so the selection layer stays aligned. */
const lightEditorTheme = Prec.highest(
  EditorView.theme(
    {
      '&': {
        fontSize: '0.875rem',
        backgroundColor: 'hsl(var(--background))',
        color: 'hsl(var(--foreground))',
        width: '100%',
      },
      '.cm-scroller': {
        fontFamily: monoFont,
        minHeight: '100%',
        overflow: 'auto',
        backgroundColor: 'hsl(var(--background))',
      },
      '.cm-content': {
        caretColor: 'hsl(var(--foreground))',
        paddingTop: '12px',
        paddingBottom: '12px',
      },
      '.cm-line': {
        padding: '0 16px',
      },
      '.cm-cursor, .cm-dropCursor': {
        borderLeftColor: 'hsl(var(--foreground))',
        borderLeftWidth: '2px',
      },
      '.cm-gutters': {
        borderRight: '1px solid hsl(var(--border))',
        backgroundColor: 'hsl(var(--muted) / 0.35)',
        color: 'hsl(var(--muted-foreground))',
      },
      '.cm-placeholder': {
        color: 'hsl(var(--muted-foreground))',
      },
      '&.cm-focused': {
        outline: 'none',
      },
      // Soft editor-style selection — keeps syntax colors readable.
      '.cm-selectionBackground': {
        background: '#E2E8F0',
      },
      '&.cm-focused > .cm-scroller > .cm-selectionLayer .cm-selectionBackground': {
        background: '#BFDBFE',
      },
    },
    { dark: false },
  ),
);

const darkEditorTheme = Prec.highest(
  EditorView.theme(
    {
      '&': {
        fontSize: '0.875rem',
        backgroundColor: '#111318',
        color: '#F8FAFC',
        width: '100%',
      },
      '.cm-scroller': {
        fontFamily: monoFont,
        minHeight: '100%',
        overflow: 'auto',
        backgroundColor: '#111318',
      },
      '.cm-content': {
        caretColor: '#FFFFFF',
        paddingTop: '12px',
        paddingBottom: '12px',
      },
      '.cm-line': {
        padding: '0 16px',
      },
      '.cm-cursor, .cm-dropCursor': {
        borderLeftColor: '#FFFFFF',
        borderLeftWidth: '2px',
      },
      '.cm-gutters': {
        borderRight: '1px solid rgba(148, 163, 184, 0.18)',
        backgroundColor: '#161A22',
        color: '#B8C4D6',
      },
      '.cm-placeholder': {
        color: '#64748B',
      },
      '&.cm-focused': {
        outline: 'none',
      },
      '.cm-selectionBackground': {
        background: 'rgba(51, 102, 153, 0.35)',
      },
      '&.cm-focused > .cm-scroller > .cm-selectionLayer .cm-selectionBackground': {
        background: 'rgba(51, 102, 153, 0.55)',
      },
    },
    { dark: true },
  ),
);

const lightHighlightStyle = HighlightStyle.define([
  { tag: t.propertyName, color: '#0F766E' },
  { tag: t.string, color: '#B45309' },
  { tag: [t.number, t.integer, t.float], color: '#1D4ED8' },
  { tag: [t.bool, t.null], color: '#7C3AED', fontWeight: '600' },
  { tag: [t.punctuation, t.separator, t.bracket], color: '#475569' },
]);

const darkHighlightStyle = HighlightStyle.define([
  { tag: t.propertyName, color: '#14E3C0', fontWeight: '700' },
  { tag: t.string, color: '#FFB020', fontWeight: '600' },
  { tag: [t.number, t.integer, t.float], color: '#4DA3FF', fontWeight: '600' },
  { tag: [t.bool, t.null], color: '#A78BFA', fontWeight: '700' },
  { tag: [t.punctuation, t.separator, t.bracket], color: '#FF6B9A', fontWeight: '700' },
]);

export function JsonCodeEditor({
  id,
  value,
  onChange,
  placeholder,
  minHeight = '420px',
  hasError = false,
}: Readonly<JsonCodeEditorProps>) {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const borderClass = hasError
    ? 'border-destructive/60'
    : isDark
      ? 'border-white/10 focus-within:border-primary/60'
      : 'border-border focus-within:border-ring';

  return (
    <div className={cn('w-full overflow-hidden rounded-lg border bg-background transition-colors', borderClass)}>
      <CodeMirror
        id={id}
        value={value}
        height={minHeight}
        placeholder={placeholder}
        theme="none"
        extensions={[
          json(),
          EditorView.lineWrapping,
          isDark ? darkEditorTheme : lightEditorTheme,
          syntaxHighlighting(isDark ? darkHighlightStyle : lightHighlightStyle),
        ]}
        onChange={onChange}
        basicSetup={{
          lineNumbers: true,
          foldGutter: false,
          drawSelection: true,
          highlightActiveLine: false,
          highlightActiveLineGutter: false,
          highlightSelectionMatches: false,
          searchKeymap: true,
          syntaxHighlighting: false,
        }}
      />
    </div>
  );
}
